import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const presets = [200, 500, 1000, 2500];

export const DonationDialog = ({ open, onOpenChange, initialAmount }) => {
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(initialAmount ? String(initialAmount) : '');
    }
  }, [open, initialAmount]);

  const handleDonate = async (e) => {
    e.preventDefault();
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 5) {
      toast({ title: 'Invalid amount', description: 'Please enter an amount of R5.00 or more.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payfast/create-payment`, {
        amount: value.toFixed(2),
        name: name || null,
        email: email || null,
        return_url: `${origin}/donation/success`,
        cancel_url: `${origin}/donation/cancelled`,
        notify_url: `${API}/payfast/itn`,
      });

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.payment_url;
      Object.entries(data.fields).forEach(([key, val]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = val;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast({
        title: 'Could not start payment',
        description: err.response?.data?.detail || 'Please try again shortly.',
        variant: 'destructive',
      });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="donation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-blue-600" /> Make a Donation
          </DialogTitle>
          <DialogDescription>
            You'll be securely redirected to PayFast to complete your donation in South African Rand (ZAR).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleDonate} className="space-y-4 pt-2">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Choose an amount</Label>
            <div className="grid grid-cols-4 gap-2">
              {presets.map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant={Number(amount) === p ? 'default' : 'outline'}
                  className={Number(amount) === p ? 'bg-blue-600' : ''}
                  onClick={() => setAmount(String(p))}
                  data-testid={`donation-preset-${p}`}
                >
                  R{p}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="donation-amount" className="text-sm font-medium text-gray-700 mb-1 block">Amount (ZAR)</Label>
            <Input
              id="donation-amount"
              type="number"
              min="5"
              step="0.01"
              placeholder="Enter amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="donation-amount-input"
              required
            />
          </div>
          <div>
            <Label htmlFor="donation-name" className="text-sm font-medium text-gray-700 mb-1 block">Name (optional)</Label>
            <Input
              id="donation-name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="donation-name-input"
            />
          </div>
          <div>
            <Label htmlFor="donation-email" className="text-sm font-medium text-gray-700 mb-1 block">Email (optional)</Label>
            <Input
              id="donation-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="donation-email-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={busy}
            data-testid="donation-submit-button"
          >
            {busy ? 'Redirecting…' : `Donate ${amount ? `R${Number(amount).toLocaleString()}` : ''}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
